import dayjs from 'dayjs'
import { useTicketUpdate } from 'features/libra/ticket/TicketUpdateContext'
import { useGeckoNotifications } from 'features/notifications'
import { useCallback, useMemo } from 'react'
import { GeckoMeasurementUnit } from 'types'
import { v4 } from 'uuid'
import { z } from 'zod'

const stringOrArraySchema = z
  .union([z.string(), z.array(z.string())])
  .transform((val) => {
    const arr = Array.isArray(val) ? val : [val]
    // Remove duplicates while preserving order
    return [...new Set(arr)] as ReadonlyArray<string>
  })

const now = () => dayjs().format('YYYY-MM-DD')

export const niiSourcesParamsSchema = z.object({
  id: z.string().default(v4().substring(0, 6)),
  commission_date: z.string().optional(),
  design_code: z.string().optional(),
  contents: z.string().optional(),
  external_coating: z.string().optional(),
  insulation: z.string().optional(),
  design_pressure_bar: z.number().optional(),
  operating_pressure_bar: z.number().optional(),
  design_temperature_c: z.number().optional(),
  max_operating_temperature_c: z.number().optional(),
  internal_inspection_interval_yrs: z.number().optional(),
  next_internal_inspection: z.string().optional(),
})

export const niiSourcesZoneParamsSchema = z.object({
  id: z.string().default(v4().substring(0, 6)),
  title: z.string().default('Untitled Zone'),
  material: z.string().optional(),
  geometry: z.string().optional(),
  length_mm: z.number().optional(),
  height_mm: z.number().optional(),
  internal_diameter_mm: z.number().optional(),
  external_diameter_mm: z.number().optional(),
  total_surface_area_sq_m: z.number().optional(),
  wall_thickness_shell_mm: z.number().optional(),
  wall_thickness_heads_mm: z.number().optional(),
  corrosion_allowance_mm: z.number().optional(),
  // Spatial bounds for AssetViz rendering
  componentId: z.string().optional(),
  bounds: z
    .object({
      x_start: z.number(),
      y_start: z.number(),
      x_end: z.number(),
      y_end: z.number(),
      unit: z.string(),
    })
    .optional(),
  calculated_area_sq_m: z.number().optional(),
  // New properties for zone management
  color: z.string().optional(),
  inspection_method: z.string().optional(),
})

const coverageSchema = z.object({
  coverage_area: z.number().default(0),
  coverage_unit: z
    .enum([
      GeckoMeasurementUnit.SquareMeters.id,
      GeckoMeasurementUnit.Percent.id,
    ])
    .default(GeckoMeasurementUnit.SquareMeters.id),
})

export const eligibilityValues = [
  'eligible',
  'not_eligible',
  'unclear',
] as const
export const niiReportTicketPropsSchema = z
  .object({
    analysisDate: z.iso.date().default(now),
    justification: z.string().optional(),
    inspectionNotes: z.string().optional(),
    eligibility: z.enum(eligibilityValues).default('unclear'),
    functionalLocation: z.string().optional(),
    params: niiSourcesParamsSchema.optional(),
    zoneParams: z.array(niiSourcesZoneParamsSchema).optional(),
    analysisResults: z.record(z.string(), z.string()).optional(),
    recommendations: z.array(z.string()).readonly().default([]),
    inspectionToCoverage: z
      .record(z.string(), coverageSchema)
      .readonly()
      .default({}),
  })
  .readonly()

export type NiiSourcesParams = z.infer<typeof niiSourcesParamsSchema>
export type NiiSourcesZoneParams = z.infer<typeof niiSourcesZoneParamsSchema>
export type NiiReportTicketProps = z.infer<typeof niiReportTicketPropsSchema>
export type NiiCoverage = z.infer<typeof coverageSchema>

export type NiiReportField = keyof NiiReportTicketProps

export const niiReportConfigSchema = z.object({
  referenceFields: z
    .partialRecord(
      niiReportTicketPropsSchema.unwrap().keyof(),
      stringOrArraySchema
    )
    .optional(),
})

export type NiiReportConfig = z.infer<typeof niiReportConfigSchema>

// NII report fields are stored directly in the flat fieldValues structure
// Each field (analysisDate, eligibility, etc.) is stored by its key in fieldValues

// Keys used for NII report data in fieldValues
// TODO: Remove all of this once we migrate this component to the workflow JSON.
export const NII_FIELD_KEYS = [
  'analysisDate',
  'justification',
  'inspectionNotes',
  'eligibility',
  'functionalLocation',
  'params',
  'zoneParams',
  'analysisResults',
  'recommendations',
  'inspectionToCoverage',
] as const satisfies ReadonlyArray<keyof NiiReportTicketProps>

export function useSaveTicketNiiData(): (
  niiData: NiiReportTicketProps
) => Promise<void> {
  const { setFieldValues } = useTicketUpdate()
  return useCallback(
    async (niiData: NiiReportTicketProps) => {
      // Spread NII data fields directly into fieldValues
      await setFieldValues(niiData as unknown as Record<string, unknown>)
    },
    [setFieldValues]
  )
}

export function useTicketNiiData(): {
  isLoading: boolean
  niiData?: NiiReportTicketProps
} {
  const { fieldValues, isLoading } = useTicketUpdate()
  const { errorNotification } = useGeckoNotifications()
  return useMemo(() => {
    // Extract NII fields from flat fieldValues
    const niiFieldValues: Record<string, unknown> = {}
    for (const key of NII_FIELD_KEYS) {
      if (key in fieldValues) {
        niiFieldValues[key] = fieldValues[key]
      }
    }

    const niiData = niiReportTicketPropsSchema.safeParse(niiFieldValues)
    if (!niiData.success) {
      errorNotification('Invalid NII data on ticket')
      return {
        isLoading,
        niiData: undefined,
      }
    }
    return {
      isLoading,
      niiData: niiData.success ? niiData.data : undefined,
    }
  }, [fieldValues, isLoading, errorNotification])
}
